<template>
  <div class="view-faqs" data-cy="faqs-sections">
    <h1 class="page_title">{{ $t('viewFaqs.page_title') }}</h1>
    <div class="search-container">
      <div class="search-bar">
        <BackstageIcon :name="Icons.SEARCH" class="search-icon" />
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          :placeholder="$t('viewFaqs.search_placeholder')"
          data-cy="faqs-search-input" />
      </div>
    </div>
    <VideoSection v-if="filteredVideoFaqs.length > 0" :video-faqs="filteredVideoFaqs" />
    <p class="title" data-cy="faqs-title">
      {{ $t('viewFaqs.title') }}
    </p>
    <Accordion :list="filteredAccordionList" :selected-group-id="selectedGroupId" />
  </div>
</template>

<script lang="ts">
import Accordion from '@/components/common/accordion.vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import VideoSection from '@/components/faq/VideoSection.vue';
import { useLocale } from '@/composables/locale';
import { globalConstants } from '@/constants/global.constants';
import { AccordionGroup } from '@/models/accordion-group.model';
import type { FaqImage } from '@/models/faq-image.model';
import { type FaqSectionModel } from '@/models/faq-section.model';
import { Icons } from '@/models/icons.enum';
import { FaqsService } from '@/services/faqs.service';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import type { FaqModel } from '@onrunning/backstage-models';
import { type Entry } from 'contentful';
import { defineComponent } from 'vue';
import { useRoute } from 'vue-router';

export default defineComponent({
  name: 'view-faqs',
  components: {
    Accordion,
    VideoSection,
    BackstageIcon,
  },
  inheritAttrs: false,
  setup() {
    const { userLanguage } = useLocale();
    const route = useRoute();

    return { userLanguage, route, Icons };
  },
  data() {
    return {
      faqsService: new FaqsService(),
      faqSections: new Array<FaqSectionModel>(),
      faqs: new Array<FaqModel>(),
      videoFaqs: new Array<FaqModel>(),
      searchQuery: '',
    };
  },
  computed: {
    selectedGroupId(): string {
      return this.route.params['id'] as string;
    },
    accordionList(): Array<AccordionGroup> {
      const accordionList = new Array<AccordionGroup>();

      this.faqSections.forEach((section) => {
        const itemIds = section.faqs.map((item) => item.externalId);
        const filteredFaqs = this.faqs.filter((faq) => itemIds.includes(faq.externalId));
        const currentLocale = this.userLanguage;
        const fallbackLocale = globalConstants.fallBackLocale;

        const items = filteredFaqs.map((item) => {
          const description = item.description[currentLocale] ?? item.description[fallbackLocale];
          const documentToHtmlStringDescription = documentToHtmlString(description);
          const rawImages = item.images?.[currentLocale] ?? item.images?.[fallbackLocale];
          const video = item.video;

          return {
            id: item.externalId,
            title: item.title[currentLocale] || item.title[fallbackLocale],
            body: documentToHtmlStringDescription,
            images: rawImages ? this.getImages(rawImages) : [],
            video,
            isOpen: false,
          };
        });

        const group: AccordionGroup = {
          id: section.externalId,
          title: section.title[currentLocale] || section.title[fallbackLocale],
          items,
        };

        accordionList.push(group);
      });

      return accordionList;
    },
    filteredAccordionList(): Array<AccordionGroup> {
      if (!this.searchQuery.trim()) {
        return this.accordionList;
      }

      const query = this.searchQuery.toLowerCase().trim();

      return this.accordionList
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => item.title.toLowerCase().includes(query)),
        }))
        .filter((group) => group.items.length > 0);
    },
    filteredVideoFaqs(): Array<FaqModel> {
      const currentLocale = this.userLanguage;
      const fallbackLocale = globalConstants.fallBackLocale;

      if (!this.searchQuery.trim()) {
        return this.videoFaqs;
      }

      const query = this.searchQuery.toLowerCase().trim();

      return this.videoFaqs.filter((faq) => {
        const title = (faq.title[currentLocale] || faq.title[fallbackLocale] || '').toLowerCase();

        return title.includes(query);
      });
    },
  },
  mounted() {
    this.fetchFaqsFromServer();
  },
  methods: {
    async fetchFaqsFromServer() {
      const [faqSectionsResult, faqsResult] = await Promise.all([
        this.faqsService.getFaqSections(),
        this.faqsService.getFaqs(),
      ]);

      if (faqSectionsResult.model) {
        this.faqSections = faqSectionsResult.model;
      }

      if (faqsResult.model) {
        this.faqs = faqsResult.model;
        this.videoFaqs = faqsResult.model.filter((faq) => {
          return !!faq.video;
        });
      }
    },
    getImages(images: Entry<any>[]): FaqImage[] {
      const currentLocale = this.userLanguage as string;
      const fallbackLocale = globalConstants.fallBackLocale as any;

      return images?.map((image: any) => ({
        id: image.sys.id,
        url: image.fields?.file[`${currentLocale}`]?.url ?? image.fields?.file[fallbackLocale]?.url,
        description: image.fields?.description[currentLocale] ?? image.fields?.description[fallbackLocale],
        title: image.fields?.title[currentLocale] ?? image.fields?.title[fallbackLocale],
      }));
    },
  },
});
</script>

<style scoped lang="scss">
.view-faqs {
  @include common-container;

  margin-bottom: 40px;

  .page_title {
    font-size: 20px;
    font-weight: 400;
    line-height: 150%;
  }

  .search-container {
    width: 100%;
    display: flex;
    justify-content: center;

    .search-bar {
      display: flex;
      width: 721px;
      height: 64px;
      padding: 16px 24px 16px 16px;
      justify-content: center;
      align-items: center;
      gap: 16px;
      border: 1px solid rgba(0, 0, 0, 0.05);
      background: rgba(0, 0, 0, 0.05);
      box-sizing: border-box;

      .search-icon {
        width: 24px;
        height: 24px;
        flex-shrink: 0;
      }

      .search-input {
        flex: 1;
        border: none;
        background: transparent;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
        outline: none;

        &::placeholder {
          color: rgba(0, 0, 0, 0.5);
        }
      }
    }
  }

  .title {
    text-align: left;
    font-size: 20px;
    margin: 16px 0;
  }
}
</style>
